import { Box, CircularProgress } from "@mui/material";
import { Typography, useMediaQuery } from "@mui/material";
import { ProjectsType } from "./types";
import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext, UserDetailsContextInterface } from "../../contexts";
import { AuthContextInterface, UserDetailsContext } from "../../contexts";
import { styles } from "./styles";
import { getAllProjects } from "../../services/projectService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { GetEnterpriseAllUsersResponse, Role, User } from "../../proto/api_pb";
import { GetProjectsResponse, Project } from "../../proto/api_pb";
import { AddProjectModal } from "../../components/Modal/AddProjectModal";
import { SingleProject } from "../../components/SingleProject/SingleProject";
import { ProjectSearchBar } from "../../components/SingleProject/ProjectSearchBar";
import { ProjectsFilterMenu } from "./ProjectsFilterMenu";
import { ContainerForGrid } from "../../components/Exolore/styles";
import { MobileScreenBottomNavigationContainer } from "../Tracks/styles";
import { MobileScreenBottomNavigation } from "../../components/TracksPageComponents/MobileScreenBottomNavigation";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import { MobileDrawer } from "../../components/MobileDrawer/MobileDrawer";
import { useNavigate } from "react-router-dom";

export const Projects: React.FC<ProjectsType> = ({
  session,
  setInbox,
  openFoldersList,
  setOpenFoldersList,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [queryValue, setQueryValue] = useState<string>("");
  const [userNames, setUserNames] = useState<string[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [selectedCollaborators, setSelectedCollaborators] =
    useState<string[]>();
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const isMobileScreen = useMediaQuery("(max-width:900px)");
  const { page, scrollContainerRef, setIsScrollEnabled, setPage } =
    useInfiniteScroll();
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const navigate = useNavigate();

  const handleUseEffect = useCallback(
    async (refresh: boolean = false) => {
      if (!userId || !enterprise?.id || !enterprise.role) return;
      setIsLoading(true);

      if (refresh) {
        setPage(0);
        setProjects([]);
      }

      const token = await getValidAccessToken();
      const res = await getAllProjects(
        token,
        userId,
        queryValue,
        selectedCollaborators,
        enterprise?.id ?? "",
        page,
        Number(enterprise?.role) ?? 0
      );

      if (res instanceof GetProjectsResponse) {
        if (refresh) {
          setProjects(res.getProjectsList());
        } else {
          setProjects((prev) => {
            const newProjects = res.getProjectsList();
            const existingProjectIds = new Set(
              prev.map((project) => project.getId())
            );
            const uniqueProjects = newProjects.filter(
              (project) => !existingProjectIds.has(project.getId())
            );
            return [...prev, ...uniqueProjects];
          });
        }

        if (res.getProjectsList().length < 8 && setIsScrollEnabled) {
          setIsScrollEnabled(false);
          if (setIsLoading) setIsLoading(false);
        } else if (setIsScrollEnabled) {
          setIsScrollEnabled(true);
        }
      }
      setIsLoading(false);
    },
    [
      enterprise?.id,
      enterprise?.role,
      getValidAccessToken,
      page,
      queryValue,
      selectedCollaborators,
      setIsScrollEnabled,
      setPage,
      userId,
    ]
  );

  useEffect(() => {
    handleUseEffect();
  }, [handleUseEffect]);

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter((user) => !user.getUsername()?.includes("dummy_ommuse"))
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setEnterpriseUsers(
          res
            .getUsersList()
            .filter((user) => !user.getUsername()?.includes("dummy_ommuse"))
        );
      }
    };
    handleUserNames();
  }, [enterprise?.id, getValidAccessToken]);

  if (!isEnterpriseAccount) {
    navigate("not-found");
    return <></>;
  }
  return (
    <>
      <MobileDrawer />
      <Box sx={{ height: "100vh", display: "flex" }}>
        <Box ref={scrollContainerRef} sx={styles.rightSideContainer}>
          <Box
            sx={{
              ...styles.projectsHeaderContainer,
              width: isMobileScreen ? "85vw" : "100%",
            }}
          >
            <Box sx={styles.headerContainer}>
              <Typography sx={{ fontWeight: "bold", fontSize: "30px" }}>
                Projects
              </Typography>
              {(Number(enterprise?.role) === Role.ROLE_ADMIN ||
                Number(enterprise?.role) === Role.ROLE_MANAGER) && (
                <AddProjectModal
                  enterpriseUsers={enterpriseUsers}
                  handleUseEffect={handleUseEffect}
                  setProjects={setProjects}
                  projects={projects}
                  setPage={setPage}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <ProjectSearchBar
                queryValue={queryValue}
                setQueryValue={setQueryValue}
                setPage={setPage}
                setProjects={setProjects}
              />
              <ProjectsFilterMenu
                userNames={userNames}
                setSelectedCollaborators={setSelectedCollaborators}
                setProjects={setProjects}
                setPage={setPage}
                selectedCollaborators={selectedCollaborators}
              />
            </Box>
          </Box>
          <ContainerForGrid
            sx={
              isMobileScreen ? { marginBottom: "90px" } : { marginTop: "15px" }
            }
          >
            {projects.map((project, index) => (
              <SingleProject
                key={index}
                project={project}
                session={session}
                setInbox={setInbox}
                handleUseEffect={handleUseEffect}
                openFoldersList={openFoldersList}
                setOpenFoldersList={setOpenFoldersList}
              />
            ))}
          </ContainerForGrid>
          {isLoading && (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          )}
          {projects.length === 0 && !isLoading && (
            <Box sx={styles.emptyMessageContainer}>
              <Typography>No projects found!</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <MobileScreenBottomNavigationContainer>
        <MobileScreenBottomNavigation />
      </MobileScreenBottomNavigationContainer>
    </>
  );
};
